import { Badge, Button, Icon, SurfaceCard } from '@react-apps/design-system';
import type { BadgeProps } from '@react-apps/design-system';
import { InfoTooltip } from '@/shared/components/info-tooltip';
import type {
  TransactionStatus,
  InvoiceStatus,
  InvoiceOrigin,
  TransactionOrigin,
  ContractStatus,
  BillingAgreementStatus,
  BillingAgreementMode,
  BillingAgreementIntervalUnit,
  DebtArrangementStatus,
  PaymentMethod,
  Tenant,
  SaleWebhookInboxStatus,
  WebhookEventType,
  Provider,
} from '../../domain/entities/sales.entity';

type BadgeVariant = NonNullable<BadgeProps['variant']>;

/** Extrai uma mensagem legível de um erro de request (axios) ou Error genérico. */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybe = err as {
      response?: { data?: { message?: unknown } };
      message?: unknown;
    };
    const apiMsg = maybe.response?.data?.message;
    if (typeof apiMsg === 'string') return apiMsg;
    if (Array.isArray(apiMsg) && typeof apiMsg[0] === 'string')
      return apiMsg[0];
    if (typeof maybe.message === 'string') return maybe.message;
  }
  return fallback;
}

/**
 * 404 é o único erro que autoriza a tela a dizer "não existe". 403, 5xx ou
 * rede caída com essa mensagem fazem o suporte achar que o id está errado.
 */
export function isNotFoundError(err: unknown): boolean {
  return (
    (err as { response?: { status?: unknown } } | null)?.response?.status ===
    404
  );
}

/**
 * Mensagem de erro para **leitura** que dispara sozinha ao abrir uma tela (a
 * página da oferta faz seis). Nos 4xx a mensagem do backend diz o que houve
 * (permissão, argumento) e passa direto; em 5xx ou sem resposta, o texto do
 * backend/axios é técnico ("Request failed with status code 500", um `E11000`
 * cru) e vale o `fallback` da tela — mesma régua de
 * `getGatewayActionErrorMessage`, sem o conselho de "tente de novo", que é
 * sobre ação e não sobre leitura.
 */
export function getLoadErrorMessage(err: unknown, fallback: string): string {
  const status = (err as { response?: { status?: unknown } } | null)?.response
    ?.status;
  if (typeof status !== 'number' || status >= 500) return fallback;
  return getApiErrorMessage(err, fallback);
}

/**
 * Mensagem de erro para as ações que falam com o gateway de pagamentos
 * (cancelar BA, resolver ciclos, sincronizar cobranças). Nos 4xx a mensagem do
 * backend **é** a informação útil (regra de negócio) e passa direto; nos 5xx ela
 * é técnica e faria o operador abrir ticket de bug, então dizemos o que ele
 * precisa saber — e isso depende de **quem** falhou:
 *
 * - `GATEWAY_API_ERROR` (502) e 503 são o gateway fora. Nada mudou e tentar de
 *   novo em alguns minutos costuma resolver.
 * - Qualquer outro 5xx é bug **nosso**, e mandar tentar de novo é conselho
 *   errado: a tentativa seguinte falha igual. Em 2026-09-17 um `E11000` do
 *   Mongo virou 500 e o operador leu "não foi possível falar com o gateway"
 *   duas vezes seguidas, enquanto o gateway respondia normalmente.
 */
export function getGatewayActionErrorMessage(
  err: unknown,
  fallback: string,
): string {
  const res = (
    err as { response?: { status?: number; data?: { error?: unknown } } } | null
  )?.response;
  const status = typeof res?.status === 'number' ? res.status : null;

  if (
    res?.data?.error === 'GATEWAY_API_ERROR' ||
    status === 502 ||
    status === 503
  ) {
    return 'Não foi possível falar com o gateway de pagamentos agora. Nada foi alterado — tente novamente em alguns minutos.';
  }
  if (status !== null && status >= 500) {
    return 'A operação falhou por um erro nosso, não do gateway. Nada foi alterado — avise o time em vez de tentar de novo.';
  }
  return getApiErrorMessage(err, fallback);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '---';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '---';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(iso));
}

export function formatDateTimeWithSeconds(
  iso: string | null | undefined,
): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(iso));
}

/** Valor monetário (assume valor já no real, como o gateway reporta). */
export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '---';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

// --- Status: rótulo PT-BR + variante de cor do Badge ---

const TRANSACTION_STATUS: Record<
  TransactionStatus,
  { label: string; variant: BadgeVariant }
> = {
  paid: { label: 'Pago', variant: 'success' },
  refunded: { label: 'Estornado', variant: 'warning' },
  chargeback: { label: 'Chargeback', variant: 'error' },
  expired: { label: 'Expirado', variant: 'default' },
  cancelled: { label: 'Cancelado', variant: 'default' },
  rejected: { label: 'Recusado', variant: 'error' },
  failed: { label: 'Falhou', variant: 'error' },
  delayed: { label: 'Atrasado', variant: 'warning' },
  disputed: { label: 'Em disputa', variant: 'warning' },
  billet_pending: { label: 'Boleto pendente', variant: 'info' },
  waiting_payment: { label: 'Aguardando pagamento', variant: 'info' },
  in_recovery: { label: 'Em recuperação', variant: 'warning' },
  blocked: { label: 'Bloqueado', variant: 'error' },
  in_analysis: { label: 'Em análise', variant: 'info' },
  voided: { label: 'Anulada', variant: 'default' },
};

const PAYMENT_METHOD: Record<
  PaymentMethod,
  { label: string; variant: BadgeVariant }
> = {
  credit_card: { label: 'Cartão de crédito', variant: 'info' },
  debit_card: { label: 'Cartão de débito', variant: 'info' },
  pix: { label: 'PIX', variant: 'success' },
  billet: { label: 'Boleto', variant: 'default' },
  paypal: { label: 'PayPal', variant: 'info' },
  free: { label: 'Gratuito', variant: 'default' },
  other: { label: 'Outro', variant: 'default' },
};

export const PAYMENT_METHOD_OPTIONS: {
  value: PaymentMethod;
  label: string;
}[] = (Object.keys(PAYMENT_METHOD) as PaymentMethod[]).map((method) => ({
  value: method,
  label: PAYMENT_METHOD[method].label,
}));

export const TRANSACTION_STATUS_OPTIONS: {
  value: TransactionStatus;
  label: string;
}[] = (Object.keys(TRANSACTION_STATUS) as TransactionStatus[]).map(
  (status) => ({
    value: status,
    label: TRANSACTION_STATUS[status].label,
  }),
);

const INVOICE_STATUS: Record<
  InvoiceStatus,
  { label: string; variant: BadgeVariant }
> = {
  pending: { label: 'Pendente', variant: 'info' },
  overdue: { label: 'Vencida', variant: 'warning' },
  paid: { label: 'Paga', variant: 'success' },
  refunded: { label: 'Estornada', variant: 'warning' },
  cancelled: { label: 'Cancelada', variant: 'default' },
  replaced: { label: 'Substituída', variant: 'default' },
};

const CONTRACT_STATUS: Record<
  ContractStatus,
  { label: string; variant: BadgeVariant }
> = {
  pending_confirmation: { label: 'Aguardando confirmação', variant: 'info' },
  active: { label: 'Ativo', variant: 'success' },
  overdue: { label: 'Em atraso', variant: 'warning' },
  finished: { label: 'Concluído', variant: 'success' },
  incompleted: { label: 'Incompleto', variant: 'warning' },
  abandoned: { label: 'Abandonado', variant: 'default' },
  cancelled: { label: 'Cancelado', variant: 'default' },
  not_converted: { label: 'Não convertido', variant: 'default' },
};

export const CONTRACT_STATUS_OPTIONS: {
  value: ContractStatus;
  label: string;
}[] = (Object.keys(CONTRACT_STATUS) as ContractStatus[]).map((status) => ({
  value: status,
  label: CONTRACT_STATUS[status].label,
}));

const BILLING_AGREEMENT_STATUS: Record<
  BillingAgreementStatus,
  { label: string; variant: BadgeVariant }
> = {
  waiting_payment: { label: 'Aguardando pagamento', variant: 'info' },
  processing: { label: 'Processando', variant: 'info' },
  trial: { label: 'Trial', variant: 'info' },
  inactive: { label: 'Inativo', variant: 'default' },
  active: { label: 'Ativo', variant: 'success' },
  pastdue: { label: 'Em atraso', variant: 'warning' },
  cancelled: { label: 'Cancelado', variant: 'default' },
  finished: { label: 'Concluído', variant: 'success' },
  failed: { label: 'Falhou', variant: 'error' },
  not_converted: { label: 'Não convertido', variant: 'default' },
};

const DEBT_ARRANGEMENT_STATUS: Record<
  DebtArrangementStatus,
  { label: string; variant: BadgeVariant }
> = {
  draft: { label: 'Rascunho', variant: 'default' },
  waiting_payment: { label: 'Aguardando pagamento', variant: 'info' },
  active: { label: 'Ativo', variant: 'success' },
  completed: { label: 'Concluído', variant: 'success' },
  broken: { label: 'Quebrado', variant: 'error' },
  cancelled: { label: 'Cancelado', variant: 'default' },
  reverted: { label: 'Revertido', variant: 'default' },
};

const SALE_WEBHOOK_INBOX_STATUS: Record<
  SaleWebhookInboxStatus,
  { label: string; variant: BadgeVariant }
> = {
  pending: { label: 'Pendente', variant: 'info' },
  processing: { label: 'Processando', variant: 'default' },
  processed: { label: 'Processado', variant: 'success' },
  ignored: { label: 'Ignorado', variant: 'warning' },
  failed: { label: 'Falhou', variant: 'error' },
  waiting_transaction: { label: 'Aguardando transação', variant: 'info' },
};

export const SALE_WEBHOOK_INBOX_STATUS_OPTIONS: {
  value: SaleWebhookInboxStatus;
  label: string;
}[] = (Object.keys(SALE_WEBHOOK_INBOX_STATUS) as SaleWebhookInboxStatus[]).map(
  (status) => ({
    value: status,
    label: SALE_WEBHOOK_INBOX_STATUS[status].label,
  }),
);

/**
 * Frase de cada `statusReason` da row do inbox. Mora aqui, e não no back, para
 * a frase mudar sem migration — como o mapa do TOTVS. `detailLabel` nomeia o
 * `statusDetail`; `null` quando ele é a mensagem de erro, que tem lugar
 * próprio no modal.
 */
const SALE_WEBHOOK_INBOX_STATUS_REASON: Record<
  string,
  { text: string; detailLabel: string | null }
> = {
  irrelevant_status: {
    text: 'Status que não exige ação (ex.: aguardando pagamento) — ignorado de propósito.',
    detailLabel: 'Status recebido',
  },
  unknown_status: {
    text: 'O gateway enviou um status que o sistema não reconhece. Avise o time de desenvolvimento.',
    detailLabel: 'Status recebido',
  },
  unhandled_event: {
    text: 'Evento do gateway que o sistema ainda não trata. Avise o time de desenvolvimento.',
    detailLabel: 'Evento',
  },
  missing_external_id: {
    text: 'O webhook chegou sem o identificador da venda ou da assinatura. Avise o time de desenvolvimento.',
    detailLabel: 'Campo ausente',
  },
  processing_error: {
    text: 'Erro ao processar o webhook. Veja os detalhes.',
    detailLabel: null,
  },
};

/** Código que esta tela ainda não conhece aparece cru, com o detalhe, em vez de sumir. */
export function describeWebhookInboxStatusReason(code: string): {
  text: string;
  detailLabel: string | null;
} {
  return Object.hasOwn(SALE_WEBHOOK_INBOX_STATUS_REASON, code)
    ? SALE_WEBHOOK_INBOX_STATUS_REASON[code]
    : { text: code, detailLabel: 'Detalhe' };
}

export function getSaleWebhookInboxStatusLabel(
  status: SaleWebhookInboxStatus,
): string {
  return SALE_WEBHOOK_INBOX_STATUS[status]?.label ?? status;
}

const WEBHOOK_EVENT_TYPE_LABELS: Record<WebhookEventType, string> = {
  transaction: 'Transação',
  subscription: 'Assinatura',
};

export function webhookEventTypeLabel(eventType: WebhookEventType): string {
  return WEBHOOK_EVENT_TYPE_LABELS[eventType] ?? eventType;
}

/**
 * Extrai o id externo (gateway) de dentro do `idempotenceSource`, ex.:
 * `guru:tx:tx-abc:approved:2026-08-04T...` → `tx-abc`. Cai pro texto bruto
 * quando o formato foge do padrão (fallback `raw` do parser, payload sem id
 * identificável — ver `extractIdempotenceSource` no monolith).
 */
export function externalIdFromIdempotenceSource(source: string): string {
  const parts = source.split(':');
  return parts.length >= 3 && parts[2] !== 'raw' ? parts[2] : source;
}

function StatusBadge({
  label,
  variant,
}: {
  label: string;
  variant: BadgeVariant;
}) {
  return <Badge variant={variant}>{label}</Badge>;
}

export function TransactionStatusBadge({
  status,
}: {
  status: TransactionStatus;
}) {
  const s = TRANSACTION_STATUS[status] ?? { label: status, variant: 'default' };
  return <StatusBadge label={s.label} variant={s.variant} />;
}

export function PaymentMethodBadge({ method }: { method: PaymentMethod }) {
  const m = PAYMENT_METHOD[method] ?? { label: method, variant: 'default' };
  return <StatusBadge label={m.label} variant={m.variant} />;
}

// --- Ícone de status (identidade visual por linha, ao lado do código) ---

const VARIANT_ICON_COLOR: Record<BadgeVariant, string> = {
  success: 'text-green-600 dark:text-green-500',
  warning: 'text-yellow-600 dark:text-yellow-500',
  error: 'text-red-600 dark:text-red-500',
  info: 'text-blue-600 dark:text-blue-500',
  default: 'text-gray-400 dark:text-gray-500',
};

const VARIANT_ICON_PATH: Record<BadgeVariant, string> = {
  success: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  warning:
    'M12 9v3.75m9.303 3.376c.866 1.5-.217 3.374-1.948 3.374H4.645c-1.73 0-2.813-1.874-1.948-3.374L10.652 3.876c.866-1.5 3.032-1.5 3.898 0l7.753 12.25zM12 15.75h.007v.008H12v-.008z',
  error: 'M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  info: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
  default: 'M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z',
};

function VariantIcon({ variant }: { variant: BadgeVariant }) {
  return (
    <Icon size="sm" strokeWidth={1.5} className={VARIANT_ICON_COLOR[variant]}>
      <path d={VARIANT_ICON_PATH[variant]} />
    </Icon>
  );
}

export function TransactionStatusIcon({
  status,
}: {
  status: TransactionStatus;
}) {
  const s = TRANSACTION_STATUS[status] ?? {
    variant: 'default' as BadgeVariant,
  };
  return <VariantIcon variant={s.variant} />;
}

// --- Tenant (BU real) — resolvido pelo backend ---

/** Rótulo PT-BR de cada tenant (espelha `TENANT_LABELS` do monolith). */
export const TENANT_LABELS: Record<Tenant, string> = {
  medq: 'MedQ',
  aristo: 'Aristo',
  cbi: 'CBI',
  bbi: 'BBI',
  'territorio-saber': 'Território Saber',
  'faculdade-primum': 'Faculdade Primum',
};

export const TENANT_OPTIONS: { value: Tenant; label: string }[] = (
  Object.keys(TENANT_LABELS) as Tenant[]
).map((tenant) => ({ value: tenant, label: TENANT_LABELS[tenant] }));

/** Rótulo do tenant, com fallback `---` quando o contrato não tem BU resolvida. */
export function tenantLabel(tenant: Tenant | null | undefined): string {
  return tenant ? TENANT_LABELS[tenant] : '---';
}

const BILLING_AGREEMENT_MODE_LABELS: Record<BillingAgreementMode, string> = {
  one_time: 'Pagamento único',
  subscription_fixed_cycles: 'Assinatura com ciclos fixos',
  subscription: 'Assinatura',
};

/** Rótulo do modo da BA, com fallback pro valor cru se surgir um modo novo. */
export function billingAgreementModeLabel(mode: BillingAgreementMode): string {
  return BILLING_AGREEMENT_MODE_LABELS[mode] ?? mode;
}

const PROVIDER_LABELS: Record<Provider, string> = {
  guru: 'Guru',
  hotmart: 'Hotmart',
  eduzz: 'Eduzz',
  apple: 'Apple',
  google: 'Google Play',
};

/** Nome do gateway como o time o chama. */
export function providerLabel(provider: Provider): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

/**
 * Onde a assinatura/venda vive de verdade — o gateway é quem cobra e quem
 * cancela quando `canCancel` é falso, então saber qual deles gerencia o
 * registro é o que diz **onde** o operador vai agir.
 */
export function GatewayBadge({ provider }: { provider: Provider }) {
  return <Badge variant="info">{providerLabel(provider)}</Badge>;
}

const INTERVAL_UNIT_LABELS: Record<BillingAgreementIntervalUnit, string> = {
  day: 'dia(s)',
  week: 'semana(s)',
  month: 'mês(es)',
  year: 'ano(s)',
};

/**
 * "Cobrada a cada" / "Intervalo de cobrança" — `null` quando a BA não tem
 * periodicidade (ex.: pagamento único), pra `Field` renderizar o traço.
 */
export function chargeIntervalLabel(
  interval: number | null,
  unit: BillingAgreementIntervalUnit | null,
): string | null {
  if (!interval || !unit) return null;
  return `${interval} ${INTERVAL_UNIT_LABELS[unit] ?? unit}`;
}

/**
 * Explica em texto por que um total é o que é. `null` quando o total é exato —
 * nesse caso não há nada a ressalvar e a tela não deve poluir.
 *
 * As duas ausências entram separadas porque só uma delas é problema: ciclo que
 * já foi cobrado e não chegou até nós é dado faltando, que alguém tem de ir
 * buscar; ciclo que ainda não venceu é o contrato seguindo seu curso. Ver
 * `missingPastCycles` em `sales.entity.ts`.
 */
export function valueSummaryCaveat(summary: {
  countedCycles: number;
  estimatedCycles: number;
  missingPastCycles: number;
  missingFutureCycles: number;
}): string | null {
  const parts: string[] = [];
  if (summary.missingPastCycles > 0) {
    parts.push(
      summary.missingPastCycles === 1
        ? '1 ciclo já cobrado não chegou até nós e ficou fora do total'
        : `${summary.missingPastCycles} ciclos já cobrados não chegaram até nós e ficaram fora do total`,
    );
  }
  if (summary.estimatedCycles > 0) {
    parts.push(
      summary.estimatedCycles === 1
        ? '1 ciclo ainda não cobrado, estimado pelo preço vigente'
        : `${summary.estimatedCycles} ciclos ainda não cobrados, estimados pelo preço vigente`,
    );
  }
  if (summary.missingFutureCycles > 0) {
    parts.push(
      summary.missingFutureCycles === 1
        ? '1 ciclo futuro ainda sem fatura, fora do total'
        : `${summary.missingFutureCycles} ciclos futuros ainda sem fatura, fora do total`,
    );
  }
  if (parts.length === 0) return null;
  return `${summary.countedCycles} ciclo(s) somado(s): ${parts.join('; ')}.`;
}

/**
 * Valor total somado das faturas, com a ressalva embutida. Um total que depende
 * de estimativa ganha `≈`, e ciclo faltando vira aviso na linha de baixo: o
 * número sozinho se passaria por exato, que é justamente o problema que essa
 * coluna existe para corrigir.
 *
 * **Duas linhas, não uma.** "faltam N ciclos" dizia a mesma coisa para o
 * contrato que perdeu 14 cobranças na adoção da conta de gateway e para o que
 * simplesmente ainda não chegou ao 12º mês — e o operador não tinha como saber
 * qual dos dois estava vendo. O passado é âmbar porque é acionável; o futuro é
 * secundário porque é só o total ainda não estar completo.
 */
export function ContractTotalValue({
  summary,
}: {
  summary: {
    totalValue: number | null;
    countedCycles: number;
    estimatedCycles: number;
    missingPastCycles: number;
    missingFutureCycles: number;
  };
}) {
  const caveat = valueSummaryCaveat(summary);

  if (summary.totalValue === null) {
    return (
      <span
        className="text-(--color-text-secondary)"
        title={caveat ?? undefined}
      >
        ---
      </span>
    );
  }

  return (
    <div className="space-y-0.5" title={caveat ?? undefined}>
      <span className="text-(--color-text-primary) whitespace-nowrap tabular-nums">
        {summary.estimatedCycles > 0 ? '≈ ' : ''}
        {formatCurrency(summary.totalValue)}
      </span>
      {summary.missingPastCycles > 0 && (
        <p className="whitespace-nowrap text-xs text-amber-800 dark:text-amber-300">
          {summary.missingPastCycles === 1
            ? '1 ciclo cobrado não sincronizado'
            : `${summary.missingPastCycles} ciclos cobrados não sincronizados`}
        </p>
      )}
      {summary.missingFutureCycles > 0 && (
        <p className="text-(--color-text-secondary) whitespace-nowrap text-xs">
          {summary.missingFutureCycles === 1
            ? '1 ciclo ainda não cobrado'
            : `${summary.missingFutureCycles} ciclos ainda não cobrados`}
        </p>
      )}
    </div>
  );
}

/**
 * "Fonte" da fatura: de onde ela veio. `gateway_invoice` é fatura que o gateway
 * emitiu e nos mandou; `synthesized` é criada por nós (o gateway cobra sem emitir
 * fatura própria, ou é ciclo futuro ainda sem cobrança); `manual` é lançamento de
 * operador.
 */
const INVOICE_ORIGIN_LABELS: Record<InvoiceOrigin, string> = {
  gateway_invoice: 'Fatura do gateway',
  synthesized: 'Gerada por nós',
  manual: 'Lançamento manual',
};

export function invoiceOriginLabel(origin: InvoiceOrigin): string {
  return INVOICE_ORIGIN_LABELS[origin] ?? origin;
}

/**
 * Por onde a transação entrou. `gateway_backfill` é a cobrança que o gateway
 * fez e nunca chegou por webhook — fomos buscá-la no histórico da assinatura.
 */
const TRANSACTION_ORIGIN_LABELS: Record<TransactionOrigin, string> = {
  gateway_webhook: 'Webhook do gateway',
  gateway_backfill: 'Histórico do gateway',
  manual: 'Manual',
};

export function transactionOriginLabel(origin: TransactionOrigin): string {
  return TRANSACTION_ORIGIN_LABELS[origin] ?? origin;
}

export const TRANSACTION_ORIGIN_OPTIONS: {
  value: TransactionOrigin;
  label: string;
}[] = (Object.keys(TRANSACTION_ORIGIN_LABELS) as TransactionOrigin[]).map(
  (origin) => ({ value: origin, label: TRANSACTION_ORIGIN_LABELS[origin] }),
);

export function InvoiceStatusBadge({ status }: { status: InvoiceStatus }) {
  const s = INVOICE_STATUS[status] ?? { label: status, variant: 'default' };
  return <StatusBadge label={s.label} variant={s.variant} />;
}

export function ContractStatusBadge({ status }: { status: ContractStatus }) {
  const s = CONTRACT_STATUS[status] ?? { label: status, variant: 'default' };
  return <StatusBadge label={s.label} variant={s.variant} />;
}

export function BillingAgreementStatusBadge({
  status,
}: {
  status: BillingAgreementStatus;
}) {
  const s = BILLING_AGREEMENT_STATUS[status] ?? {
    label: status,
    variant: 'default' as BadgeVariant,
  };
  return <StatusBadge label={s.label} variant={s.variant} />;
}

export function DebtArrangementStatusBadge({
  status,
}: {
  status: DebtArrangementStatus;
}) {
  const s = DEBT_ARRANGEMENT_STATUS[status] ?? {
    label: status,
    variant: 'default' as BadgeVariant,
  };
  return <StatusBadge label={s.label} variant={s.variant} />;
}

/**
 * Com motivo, ganha um ⓘ ao lado: o badge não é interativo, então o tooltip
 * nele não teria affordance nem seria alcançável pelo Tab. No `failed` o
 * tooltip mostra só a frase — a mensagem inteira fica no modal de detalhes.
 */
export function WebhookInboxStatusBadge({
  status,
  statusReason,
  statusDetail,
}: {
  status: SaleWebhookInboxStatus;
  statusReason?: string | null;
  statusDetail?: string | null;
}) {
  const s = SALE_WEBHOOK_INBOX_STATUS[status] ?? {
    label: status,
    variant: 'default' as BadgeVariant,
  };
  const badge = <StatusBadge label={s.label} variant={s.variant} />;
  if (!statusReason) return badge;
  const reason = describeWebhookInboxStatusReason(statusReason);
  const showDetail = status !== 'failed' && reason.detailLabel && statusDetail;
  return (
    <span className="inline-flex items-center gap-1">
      {badge}
      <InfoTooltip
        label="Motivo do status"
        message={
          <span className="block space-y-1">
            <span className="block">{reason.text}</span>
            {showDetail && (
              <span className="block">
                {reason.detailLabel}: <Mono>{statusDetail}</Mono>
              </span>
            )}
          </span>
        }
      />
    </span>
  );
}

// --- Banners de feedback dos modais de ação ---

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
      {message}
    </div>
  );
}

export function SuccessBanner({ message }: { message: string }) {
  return (
    <div className="rounded-lg bg-green-50 p-3 text-sm text-green-700 dark:bg-green-950/40 dark:text-green-300">
      {message}
    </div>
  );
}

export function InfoNotice({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-3 rounded-lg bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-950/40 dark:text-blue-300">
      <svg
        className="mt-0.5 h-5 w-5 shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
        />
      </svg>
      <p>{children}</p>
    </div>
  );
}

// --- Blocos de layout reutilizados nas abas de detalhe ---

export function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="border-(--color-border) space-y-1 border-b pb-3">
      <p className="text-(--color-text-secondary) text-xs font-medium uppercase tracking-wide">
        {label}
      </p>
      <div className="text-(--color-text-primary) text-sm">
        {value === null || value === undefined || value === '' ? (
          <span className="text-(--color-text-secondary)">---</span>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

export function Mono({ children }: { children: React.ReactNode }) {
  return <span className="break-all font-mono text-xs">{children}</span>;
}

/**
 * Renderiza um valor de `providerMetadata` (blob opaco) como texto. Objeto sai
 * indentado — e string que é JSON também, porque há gateway que manda o objeto
 * já serializado dentro do campo.
 */
function stringifyMeta(value: unknown): { text: string; json: boolean } {
  if (value === null || value === undefined)
    return { text: '---', json: false };
  if (typeof value === 'string' && /^\s*[[{]/.test(value)) {
    try {
      value = JSON.parse(value);
    } catch {
      // não era JSON: segue como string
    }
  }
  if (typeof value === 'object' && value !== null) {
    return { text: JSON.stringify(value, null, 2), json: true };
  }
  return { text: String(value), json: false };
}

/**
 * Bloco de `providerMetadata`: o que o gateway mandou, como ele mandou.
 *
 * Ele **não** se parece com os `Field` vizinhos de propósito. Campo nosso tem
 * rótulo em português, curado, com significado que a gente sustenta; metadado é
 * chave de terceiro, na grafia do terceiro, que muda quando o provedor quiser.
 * Renderizados pelo mesmo `Field`, os dois viravam uma lista só — e a caixa alta
 * do rótulo ainda comia as fronteiras de palavra da chave crua
 * (`productMarketplaceId` virava `PRODUCTMARKETPLACEID`), então nem como chave
 * ela lia bem. Daí o painel recuado, a borda tracejada e a chave em mono na
 * grafia original.
 *
 * `heading={null}` quando o card que o contém já anuncia a seção.
 */
export function ProviderMetadataBlock({
  entries,
  heading = 'Metadata do provedor',
}: {
  entries: [string, unknown][];
  heading?: string | null;
}) {
  if (entries.length === 0) return null;

  return (
    <section className="border-(--color-border) bg-(--color-bg-secondary) space-y-3 rounded-lg border border-dashed p-4">
      <header className="space-y-1">
        {heading && (
          <p className="text-(--color-text-secondary) text-xs font-semibold uppercase tracking-wide">
            {heading}
          </p>
        )}
        <p className="text-(--color-text-secondary) text-xs">
          Campos crus do gateway — o nome e o valor são dele, não nossos.
        </p>
      </header>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        {entries.map(([key, value]) => {
          const { text, json } = stringifyMeta(value);
          return (
            <div
              key={key}
              className={`min-w-0 space-y-0.5 ${json ? 'sm:col-span-2' : ''}`}
            >
              <dt className="text-(--color-text-secondary) break-all font-mono text-xs font-semibold">
                {key}
              </dt>
              <dd
                className={`break-all font-mono text-xs ${
                  json ? 'whitespace-pre-wrap' : ''
                } ${
                  text === '---'
                    ? 'text-(--color-text-secondary)'
                    : 'text-(--color-text-primary)'
                }`}
              >
                {text}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

export function SectionCard({
  title,
  action,
  children,
}: {
  /** `ReactNode` para caber título + marcador (ex.: o gateway da BA). */
  title: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <SurfaceCard className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-(--color-text-primary) flex items-center gap-2 text-sm font-semibold">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </SurfaceCard>
  );
}

export function SkeletonCard({ rows = 4 }: { rows?: number }) {
  return (
    <SurfaceCard className="space-y-4">
      <div className="h-4 w-40 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700" />
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="h-4 animate-pulse rounded bg-neutral-100 dark:bg-neutral-800"
            style={{ width: `${60 + (i % 3) * 15}%` }}
          />
        ))}
      </div>
    </SurfaceCard>
  );
}

/** Com `onRetry`, ganha "Tentar de novo": falha transitória não exige recarregar a página. */
export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <SurfaceCard padding="lg" className="text-center">
      <p className="text-(--color-text-secondary) text-sm">{message}</p>
      {onRetry && (
        <Button variant="secondary" className="mt-3" onClick={onRetry}>
          Tentar de novo
        </Button>
      )}
    </SurfaceCard>
  );
}

/** Texto de erro inline padronizado (claro + escuro) — usado pelo `ConfirmModal`. */
export function ErrorText({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-red-600 dark:text-red-400">{children}</p>;
}
